import { t } from "@travily/i18n";
import type ImageType from "@travily/types/shared/Image";
import { Card, CardAuthor, CardDescription, CardFavoriteButton, CardImage, CardTitle } from "@ui/components/Card";
import { Carousel } from "@ui/components/Carousel";
import { ExitIcon } from "@ui/components/Icon";
import { MAP_CHROME_ROUND_BUTTON } from "@ui/components/Map/chrome";
import type { ReactNode } from "react";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";

export interface MapPinCardAuthor {
  avatar?: ImageType | null;
  href: string;
  initial: string;
  name: string;
  profile_type: string;
}

export interface MapPinCardProps {
  href: string;
  linkLabel: string;
  title: string;
  image?: ImageType | null;
  /** Multiple images — renders a carousel when length > 1. */
  images?: ImageType[];
  /** Fallback shown when there's no image (e.g. a member's initials avatar). */
  imageFallback?: ReactNode;
  /** Optional creator row (avatar + name) under the title. */
  author?: MapPinCardAuthor;
  description?: string | null;
  price?: string | null;
  /** Draws the price as the estimate it is: lighter than a figure somebody
   *  actually paid. */
  priceEstimated?: boolean;
  /** How the price was arrived at — the price's tooltip and its label for a
   *  reader who can't see the weight it is drawn in. */
  priceNote?: string;
  favorited?: boolean;
  /** When set, a favorite heart is shown next to the close button. */
  onFavoriteToggle?: () => void;
  onClose: () => void;
  /** Root width utility. Defaults to the 320px popup width; pass `w-full` for the mobile sheet card. */
  widthClassName?: string;
  /** `sizes` hint for the image/carousel. Defaults to `320px`; widen (e.g. `100vw`) for full-width. */
  imageSizes?: string;
}

/**
 * Map pin popover card (Figma `Card / Discovery / Map`): a full-bleed image or
 * carousel with a favorite heart + close button overlaid top-right, above a
 * padded title / creator / description / price block. Matches the listing card
 * visually while owning the popover-specific chrome.
 */
export function MapPinCard({
  href,
  linkLabel,
  title,
  image,
  images,
  imageFallback,
  author,
  description,
  price,
  priceEstimated,
  priceNote,
  favorited,
  onFavoriteToggle,
  onClose,
  widthClassName,
  imageSizes,
}: MapPinCardProps) {
  const showCarousel = images != null && images.length > 1;
  const sizes = imageSizes ?? "320px";

  return (
    <div className={`relative ${widthClassName ?? "w-[320px]"}`}>
      <Card className="gap-0 overflow-hidden rounded-3xl bg-white shadow-lg" href={href} linkLabel={linkLabel}>
        {showCarousel ? (
          <Carousel
            aspect="landscape"
            controls="visible"
            images={images}
            inset="sm"
            mobile="carousel"
            size="compact"
            sizes={sizes}
          />
        ) : (
          <CardImage
            alt={title}
            className="aspect-[5/3] rounded-none"
            fallback={imageFallback}
            image={image}
            sizes={sizes}
          />
        )}
        <div className="flex flex-col gap-2 px-5 pt-4 pb-5">
          <div className="flex flex-col gap-1">
            <CardTitle theme="compact">{title}</CardTitle>
            {author ? (
              <CardAuthor
                avatar={<AccountAvatar account={author} shape="circle" size="24" />}
                href={author.href}
                name={author.name}
              />
            ) : null}
          </div>
          {description || price ? (
            <div className="flex flex-col gap-2">
              {description ? <CardDescription theme="compact">{description}</CardDescription> : null}
              {price ? (
                <p
                  className={
                    priceEstimated
                      ? "font-normal text-[15px] text-neutral-600 leading-none"
                      : "font-medium text-[15px] text-neutral-900 leading-none"
                  }
                  title={priceNote}
                >
                  {price}
                  {priceNote ? <span className="sr-only">{priceNote}</span> : null}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>
      <div className="absolute top-3 right-3 z-30 flex items-center gap-2">
        {onFavoriteToggle ? (
          <CardFavoriteButton favorited={favorited} name={title} onToggle={onFavoriteToggle} />
        ) : null}
        <button
          aria-label={t("components.map.close_popup")}
          className={MAP_CHROME_ROUND_BUTTON}
          onClick={onClose}
          type="button"
        >
          <ExitIcon aria-hidden className="size-4" />
        </button>
      </div>
    </div>
  );
}
